import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styles from './AdminShared.module.scss';
import {
    ContentProcessorConfigurationDto,
    ContentProcessorConfigurationUpdateDto,
    ContentProcessorTranscodeTemplateDto,
    ContentProcessorTranscodeTemplateUpdateDto,
    ContentProcessorWorkerDto,
    ContentProcessorWorkerUpdateDto
} from '../../../types/admin.types';
import {
    createContentProcessorTranscodeTemplate,
    deleteContentProcessorWorker,
    fetchContentProcessorConfiguration,
    fetchContentProcessorTranscodeTemplates,
    fetchContentProcessorWorkers,
    pauseContentProcessor,
    resumeContentProcessor,
    updateContentProcessorConfiguration,
    updateContentProcessorTranscodeTemplate,
    updateContentProcessorWorker
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { Routes } from '../../../types/constants';
import { isSuperAdmin } from '../../../utils/roles';

const globalTargetId = 'global';
const workerRoles = [
    { value: 0, label: 'Standard' },
    { value: 1, label: 'Mobile Backfill' }
];
const standardRole = 0;
const mobileBackfillRole = 1;
const mp4Format = 2;
const mobileMp4Format = 3;
const mp4ContainerFormat = 0;
const libx264VideoCodec = 0;
const yuv420pPixelFormat = 0;
const baselineProfile = 0;
const mainProfile = 1;
const highProfile = 2;
const aacAudioCodec = 0;
const level3_0 = 30;
const level3_1 = 31;
const level4_0 = 40;
const level4_1 = 41;
const level4_2 = 42;

const templateLimits = {
    videoBitrate: { min: 100, max: 50000 },
    frameRate: { min: 1, max: 240 },
    keyFrameInterval: { min: 1, max: 600 },
    sceneChangeThreshold: { min: 0, max: 100 },
    audioBitrate: { min: 32, max: 512 },
    audioChannels: { min: 1, max: 8 },
    audioSampleRate: { min: 8000, max: 192000 },
    progressInterval: { min: 1, max: 3600 },
    percentDelta: { min: 0, max: 100 }
};

const outputHeightOptions = [360, 540, 720, 1080];
const containerFormatOptions = [{ value: mp4ContainerFormat, label: 'MP4 (.mp4)' }];
const videoCodecOptions = [{ value: libx264VideoCodec, label: 'H.264 / libx264' }];
const pixelFormatOptions = [{ value: yuv420pPixelFormat, label: 'yuv420p' }];
const videoProfileOptions = [
    { value: baselineProfile, label: 'baseline' },
    { value: mainProfile, label: 'main' },
    { value: highProfile, label: 'high' }
];
const videoLevelOptions = [
    { value: level3_0, label: '3.0' },
    { value: level3_1, label: '3.1' },
    { value: level4_0, label: '4.0' },
    { value: level4_1, label: '4.1' },
    { value: level4_2, label: '4.2' }
];
const audioCodecOptions = [{ value: aacAudioCodec, label: 'AAC' }];

const standardTemplatePreset: ContentProcessorTranscodeTemplateUpdateDto = {
    Name: 'Standard 1080p MP4 Copy',
    WorkerMode: standardRole,
    OutputFormat: mp4Format,
    IsActive: true,
    IsDefault: false,
    TranscodeSettings: {
        ContainerFormat: mp4ContainerFormat,
        OutputHeight: 1080,
        VideoCodec: libx264VideoCodec,
        VideoBitrateKilobits: 5500,
        VideoMaxRateKilobits: 7500,
        VideoBufferSizeKilobits: 15000,
        FrameRate: 30,
        KeyFrameIntervalFrames: 60,
        SceneChangeThreshold: 0,
        PixelFormat: yuv420pPixelFormat,
        VideoProfile: highProfile,
        VideoLevel: level4_1,
        AudioCodec: aacAudioCodec,
        AudioBitrateKilobits: 128,
        AudioChannels: 2,
        AudioSampleRate: 48000,
        EnableFastStart: true
    },
    ProgressSettings: {
        EventsEnabled: true,
        IntervalSeconds: 15,
        MinPercentDelta: 1,
        EmitFirstEvent: true
    },
    Notes: 'Default desktop playback encoder.'
};

const mobileTemplatePreset: ContentProcessorTranscodeTemplateUpdateDto = {
    Name: 'Mobile 540p MP4 Copy',
    WorkerMode: mobileBackfillRole,
    OutputFormat: mobileMp4Format,
    IsActive: true,
    IsDefault: false,
    TranscodeSettings: {
        ContainerFormat: mp4ContainerFormat,
        OutputHeight: 540,
        VideoCodec: libx264VideoCodec,
        VideoBitrateKilobits: 1400,
        VideoMaxRateKilobits: 1800,
        VideoBufferSizeKilobits: 3600,
        FrameRate: 30,
        KeyFrameIntervalFrames: 60,
        SceneChangeThreshold: 0,
        PixelFormat: yuv420pPixelFormat,
        VideoProfile: mainProfile,
        VideoLevel: level3_1,
        AudioCodec: aacAudioCodec,
        AudioBitrateKilobits: 96,
        AudioChannels: 2,
        AudioSampleRate: 48000,
        EnableFastStart: true
    },
    ProgressSettings: {
        EventsEnabled: true,
        IntervalSeconds: 15,
        MinPercentDelta: 1,
        EmitFirstEvent: true
    },
    Notes: 'Default constrained-network mobile playback encoder.'
};

const formatWorkerRole = (value?: number | null) => workerRoles.find((role) => role.value === value)?.label ?? 'Not configured';

const isTemplateAssignable = (template: ContentProcessorTranscodeTemplateDto) => {
    if (!template.IsActive) {
        return false;
    }

    if (template.WorkerMode === standardRole) {
        return template.OutputFormat === mp4Format;
    }

    if (template.WorkerMode === mobileBackfillRole) {
        return template.OutputFormat === mobileMp4Format;
    }

    return false;
};

const isTemplateIdAssignable = (templates: ContentProcessorTranscodeTemplateDto[], templateId?: string | null) => (
    Boolean(templateId && templates.some((template) => template.Id === templateId && isTemplateAssignable(template)))
);

const formatWorkerTemplateOption = (template: ContentProcessorTranscodeTemplateDto) => {
    const suffix = isTemplateAssignable(template) ? '' : ' (unavailable)';
    return `${template.Name} (${formatWorkerRole(template.WorkerMode)})${suffix}`;
};

const formatOutputFormat = (value?: number | null) => {
    if (value === 0) {
        return 'Unprocessed';
    }

    if (value === 1) {
        return 'WebM';
    }

    if (value === 2) {
        return 'MP4';
    }

    if (value === 3) {
        return 'Mobile MP4';
    }

    return '-';
};

const cloneTemplateForm = (template: ContentProcessorTranscodeTemplateUpdateDto): ContentProcessorTranscodeTemplateUpdateDto => ({
    ...template,
    TranscodeSettings: { ...template.TranscodeSettings },
    ProgressSettings: { ...template.ProgressSettings }
});

const buildTemplateForm = (template: ContentProcessorTranscodeTemplateDto): ContentProcessorTranscodeTemplateUpdateDto => ({
    Name: template.Name,
    WorkerMode: template.WorkerMode ?? standardRole,
    OutputFormat: template.OutputFormat,
    IsActive: template.IsActive,
    IsDefault: template.IsDefault,
    TranscodeSettings: { ...template.TranscodeSettings },
    ProgressSettings: { ...template.ProgressSettings },
    Notes: template.Notes ?? ''
});

const getTemplatePreset = (workerMode: number) => cloneTemplateForm(
    workerMode === mobileBackfillRole ? mobileTemplatePreset : standardTemplatePreset
);

const refreshWorkerList = async (
    token: string | null | undefined,
    setWorkers: React.Dispatch<React.SetStateAction<ContentProcessorWorkerDto[]>>
) => {
    const workersResponse = await fetchContentProcessorWorkers(token);
    setWorkers(workersResponse);
};

const formatVideoProfile = (value?: number | null) => videoProfileOptions.find((option) => option.value === value)?.label ?? '-';
const formatVideoLevel = (value?: number | null) => videoLevelOptions.find((option) => option.value === value)?.label ?? '-';
const formatContainerFormat = (value?: number | null) => containerFormatOptions.find((option) => option.value === value)?.label ?? '-';

const toLocalDateTimeInput = (value?: string | null) => {
    if (!value) {
        return '';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
    return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromLocalDateTimeInput = (value?: string | null) => {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const buildWorkerUpdatePayload = (
    workerForm: ContentProcessorWorkerUpdateDto,
    templates: ContentProcessorTranscodeTemplateDto[],
    pauseUntilUtc = fromLocalDateTimeInput(workerForm.PauseUntilUtc)
): ContentProcessorWorkerUpdateDto => ({
    ...workerForm,
    AssignedTranscodeTemplateId: isTemplateIdAssignable(templates, workerForm.AssignedTranscodeTemplateId)
        ? workerForm.AssignedTranscodeTemplateId
        : null,
    PauseUntilUtc: pauseUntilUtc
});

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const buildForm = (configuration: ContentProcessorConfigurationDto): ContentProcessorConfigurationUpdateDto => ({
    IsPollingEnabled: configuration.IsPollingEnabled,
    PollingIntervalSeconds: configuration.PollingIntervalSeconds,
    PauseUntilUtc: toLocalDateTimeInput(configuration.PauseUntilUtc),
    MaxConcurrentJobs: configuration.MaxConcurrentJobs,
    LeaseTimeoutSeconds: configuration.LeaseTimeoutSeconds,
    HeartbeatTimeoutSeconds: configuration.HeartbeatTimeoutSeconds,
    Notes: configuration.Notes ?? ''
});

const buildWorkerForm = (worker: ContentProcessorWorkerDto): ContentProcessorWorkerUpdateDto => ({
    DisplayName: worker.DisplayName ?? '',
    IsEnabled: worker.IsEnabled,
    AssignedTranscodeTemplateId: worker.AssignedTranscodeTemplateId ?? null,
    PollingIntervalSeconds: worker.PollingIntervalSeconds ?? null,
    PauseUntilUtc: toLocalDateTimeInput(worker.PauseUntilUtc),
    MaxConcurrentJobs: worker.MaxConcurrentJobs ?? null,
    LeaseTimeoutSeconds: worker.LeaseTimeoutSeconds ?? null,
    HeartbeatTimeoutSeconds: worker.HeartbeatTimeoutSeconds ?? null,
    Notes: worker.Notes ?? ''
});

const toOptionalNumber = (value: string) => value.trim().length === 0 ? null : Number(value);

const AdminContentProcessingConfig = () => {
    const { getUserInfo } = useUserState();
    const user = getUserInfo();
    const token = user?.token;
    const canManage = isSuperAdmin(user?.roles);
    const [configuration, setConfiguration] = useState<ContentProcessorConfigurationDto | null>(null);
    const [workers, setWorkers] = useState<ContentProcessorWorkerDto[]>([]);
    const [templates, setTemplates] = useState<ContentProcessorTranscodeTemplateDto[]>([]);
    const [selectedTargetId, setSelectedTargetId] = useState(globalTargetId);
    const [form, setForm] = useState<ContentProcessorConfigurationUpdateDto | null>(null);
    const [workerForm, setWorkerForm] = useState<ContentProcessorWorkerUpdateDto | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [templateForm, setTemplateForm] = useState<ContentProcessorTranscodeTemplateUpdateDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const selectedWorker = useMemo(
        () => workers.find((worker) => worker.WorkerId === selectedTargetId) ?? null,
        [selectedTargetId, workers]
    );
    const isGlobalSelected = selectedTargetId === globalTargetId;
    const selectedWorkerFormTemplate = useMemo(
        () => templates.find((template) => template.Id === workerForm?.AssignedTranscodeTemplateId) ?? null,
        [templates, workerForm?.AssignedTranscodeTemplateId]
    );

    const loadConfiguration = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const [configurationResponse, workersResponse, templatesResponse] = await Promise.all([
                fetchContentProcessorConfiguration(token),
                fetchContentProcessorWorkers(token),
                fetchContentProcessorTranscodeTemplates(token)
            ]);
            setConfiguration(configurationResponse);
            setWorkers(workersResponse);
            setTemplates(templatesResponse);
            setForm(buildForm(configurationResponse));
        } catch {
            setErrorMessage('Unable to load content processor configuration.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (canManage) {
            loadConfiguration();
        }
    }, [canManage, loadConfiguration]);

    useEffect(() => {
        setWorkerForm(selectedWorker ? buildWorkerForm(selectedWorker) : null);
        if (selectedTargetId !== globalTargetId && workers.length > 0 && !selectedWorker) {
            setSelectedTargetId(globalTargetId);
        }
    }, [selectedTargetId, selectedWorker, workers.length]);

    if (!canManage) {
        return <Navigate to={Routes.ADMIN_ANALYTICS} replace />;
    }

    const replaceWorker = (worker: ContentProcessorWorkerDto) => {
        setWorkers((current) => current.map((item) => item.WorkerId === worker.WorkerId ? worker : item));
        setWorkerForm(buildWorkerForm(worker));
    };

    const selectTemplateForEdit = (templateId: string) => {
        const template = templates.find((item) => item.Id === templateId) ?? null;
        setSelectedTemplateId(template?.Id ?? null);
        setTemplateForm(template ? buildTemplateForm(template) : null);
    };

    const startTemplateFromPreset = (workerMode: number) => {
        setSelectedTemplateId(null);
        setTemplateForm(getTemplatePreset(workerMode));
    };

    const updateTemplateWorkerMode = (workerMode: number) => {
        if (!templateForm) {
            return;
        }

        const preset = getTemplatePreset(workerMode);
        setTemplateForm({
            ...templateForm,
            WorkerMode: workerMode,
            OutputFormat: preset.OutputFormat,
            TranscodeSettings: {
                ...preset.TranscodeSettings
            },
            ProgressSettings: {
                ...preset.ProgressSettings
            }
        });
    };

    const saveTemplate = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!templateForm) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const payload = cloneTemplateForm(templateForm);
            const response = selectedTemplateId
                ? await updateContentProcessorTranscodeTemplate(selectedTemplateId, payload, token)
                : await createContentProcessorTranscodeTemplate(payload, token);
            setTemplates((current) => selectedTemplateId
                ? current.map((template) => template.Id === response.Id ? response : template)
                : [...current, response]);
            setSelectedTemplateId(response.Id);
            setTemplateForm(buildTemplateForm(response));
            setSuccessMessage(`Template ${response.Name} saved.`);
            try {
                await refreshWorkerList(token, setWorkers);
            } catch {
                setSuccessMessage(`Template ${response.Name} saved, but worker status could not be refreshed.`);
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to save transcode template.');
        } finally {
            setIsSaving(false);
        }
    };

    const saveConfiguration = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isGlobalSelected) {
            await saveGlobalConfiguration();
            return;
        }

        await saveWorkerConfiguration();
    };

    const saveGlobalConfiguration = async () => {
        if (!form) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const payload: ContentProcessorConfigurationUpdateDto = {
                ...form,
                PauseUntilUtc: fromLocalDateTimeInput(form.PauseUntilUtc)
            };
            const response = await updateContentProcessorConfiguration(payload, token);
            setConfiguration(response);
            setForm(buildForm(response));
            setSuccessMessage('Global content processor configuration saved.');
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to save configuration.');
        } finally {
            setIsSaving(false);
        }
    };

    const saveWorkerConfiguration = async () => {
        if (!selectedWorker || !workerForm) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const payload = buildWorkerUpdatePayload(workerForm, templates);
            const response = await updateContentProcessorWorker(selectedWorker.WorkerId, payload, token);
            replaceWorker(response);
            setSuccessMessage(`Worker ${response.DisplayName || response.WorkerId} configuration saved.`);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to save worker configuration.');
        } finally {
            setIsSaving(false);
        }
    };

    const updateSelectedWorkerPause = async (pauseUntilUtc: string | null, message: string) => {
        if (!selectedWorker || !workerForm) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const payload = buildWorkerUpdatePayload({
                ...workerForm,
                Notes: message
            }, templates, pauseUntilUtc);
            const response = await updateContentProcessorWorker(selectedWorker.WorkerId, payload, token);
            replaceWorker(response);
            setSuccessMessage(message);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to update worker pause state.');
        } finally {
            setIsSaving(false);
        }
    };

    const pauseForMinutes = async (minutes: number) => {
        const pauseUntil = new Date(Date.now() + minutes * 60 * 1000).toISOString();
        if (!isGlobalSelected) {
            await updateSelectedWorkerPause(pauseUntil, `Worker paused for ${minutes} minutes.`);
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const response = await pauseContentProcessor({
                PauseUntilUtc: pauseUntil,
                Notes: `Paused from admin for ${minutes} minutes`
            }, token);
            setConfiguration(response);
            setForm(buildForm(response));
            setSuccessMessage(`Global content processing paused for ${minutes} minutes.`);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to pause content processor.');
        } finally {
            setIsSaving(false);
        }
    };

    const resumeNow = async () => {
        if (!isGlobalSelected) {
            await updateSelectedWorkerPause(null, 'Worker resumed.');
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const response = await resumeContentProcessor(token);
            setConfiguration(response);
            setForm(buildForm(response));
            setSuccessMessage('Global content processing resumed.');
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to resume content processor.');
        } finally {
            setIsSaving(false);
        }
    };

    const hideSelectedWorker = async () => {
        if (!selectedWorker || !window.confirm(`Hide worker ${selectedWorker.WorkerId}? It will reappear if the same worker id reconnects.`)) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            await deleteContentProcessorWorker(selectedWorker.WorkerId, token);
            setWorkers((current) => current.filter((worker) => worker.WorkerId !== selectedWorker.WorkerId));
            setSelectedTargetId(globalTargetId);
            setWorkerForm(null);
            setSuccessMessage(`Worker ${selectedWorker.WorkerId} hidden.`);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to hide worker.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderGlobalFields = () => form ? (
        <>
            <div className={styles.field}>
                <label htmlFor="processor-enabled">
                    <input
                        id="processor-enabled"
                        type="checkbox"
                        checked={form.IsPollingEnabled}
                        onChange={(event) => setForm({ ...form, IsPollingEnabled: event.target.checked })}
                    />
                    Enable processing globally
                </label>
            </div>
            <div className={styles.field}>
                <label htmlFor="processor-poll-interval">Idle check interval seconds</label>
                <input
                    id="processor-poll-interval"
                    type="number"
                    min="5"
                    max="3600"
                    value={form.PollingIntervalSeconds}
                    onChange={(event) => setForm({ ...form, PollingIntervalSeconds: Number(event.target.value) })}
                />
            </div>
            <div className={styles.field}>
                <label htmlFor="processor-pause-until">Pause until</label>
                <input
                    id="processor-pause-until"
                    type="datetime-local"
                    value={form.PauseUntilUtc ?? ''}
                    onChange={(event) => setForm({ ...form, PauseUntilUtc: event.target.value })}
                />
            </div>
            <div className={styles.field}>
                <label htmlFor="processor-notes">Notes</label>
                <textarea
                    id="processor-notes"
                    value={form.Notes ?? ''}
                    onChange={(event) => setForm({ ...form, Notes: event.target.value })}
                    maxLength={2000}
                />
            </div>
        </>
    ) : null;

    const renderWorkerFields = () => {
        if (!workerForm || !selectedWorker) {
            return null;
        }

        const selectedTemplateId = workerForm.AssignedTranscodeTemplateId;
        const selectedTemplateIsAssignable = isTemplateIdAssignable(templates, selectedTemplateId);
        const workerTemplateOptions = templates.filter((template) => (
            isTemplateAssignable(template) || template.Id === selectedTemplateId
        ));

        return (
            <>
            <div className={styles.field}>
                <label htmlFor="worker-display-name">Display name</label>
                <input
                    id="worker-display-name"
                    value={workerForm.DisplayName ?? ''}
                    onChange={(event) => setWorkerForm({ ...workerForm, DisplayName: event.target.value })}
                    maxLength={200}
                    placeholder={selectedWorker.WorkerId}
                />
            </div>
            <div className={styles.field}>
                <label htmlFor="worker-enabled">
                    <input
                        id="worker-enabled"
                        type="checkbox"
                        checked={workerForm.IsEnabled}
                        onChange={(event) => setWorkerForm({ ...workerForm, IsEnabled: event.target.checked })}
                    />
                    Enable this worker
                </label>
            </div>
            <div className={styles.field}>
                <label>Worker mode</label>
                <div aria-label="Selected worker mode">{formatWorkerRole(workerForm.AssignedTranscodeTemplateId ? selectedWorkerFormTemplate?.WorkerMode : null)}</div>
            </div>
            <div className={styles.field}>
                <label htmlFor="worker-template">Transcode template</label>
                <select
                    id="worker-template"
                    value={workerForm.AssignedTranscodeTemplateId ?? ''}
                    onChange={(event) => setWorkerForm({
                        ...workerForm,
                        AssignedTranscodeTemplateId: event.target.value === '' ? null : event.target.value
                    })}
                >
                    <option value="">Not configured</option>
                    {workerTemplateOptions.map((template) => (
                        <option key={template.Id} value={template.Id}>
                            {formatWorkerTemplateOption(template)}
                        </option>
                    ))}
                    {selectedTemplateId && !selectedWorkerFormTemplate ? (
                        <option value={selectedTemplateId}>Missing template (unavailable)</option>
                    ) : null}
                </select>
                {selectedTemplateId && !selectedTemplateIsAssignable ? (
                    <span className={styles.muted}>Current template is unavailable. Choose another template or save to clear it.</span>
                ) : null}
            </div>
            <div className={styles.field}>
                <label htmlFor="worker-poll-interval">Idle check interval override seconds</label>
                <input
                    id="worker-poll-interval"
                    type="number"
                    min="5"
                    max="3600"
                    value={workerForm.PollingIntervalSeconds ?? ''}
                    onChange={(event) => setWorkerForm({ ...workerForm, PollingIntervalSeconds: toOptionalNumber(event.target.value) })}
                    placeholder={`${configuration?.PollingIntervalSeconds ?? 300}`}
                />
            </div>
            <div className={styles.field}>
                <label htmlFor="worker-pause-until">Pause this worker until</label>
                <input
                    id="worker-pause-until"
                    type="datetime-local"
                    value={workerForm.PauseUntilUtc ?? ''}
                    onChange={(event) => setWorkerForm({ ...workerForm, PauseUntilUtc: event.target.value })}
                />
            </div>
            <div className={styles.field}>
                <label htmlFor="worker-notes">Worker notes</label>
                <textarea
                    id="worker-notes"
                    value={workerForm.Notes ?? ''}
                    onChange={(event) => setWorkerForm({ ...workerForm, Notes: event.target.value })}
                    maxLength={2000}
                />
            </div>
        </>
        );
    };

    const updateTemplateTranscodeSettings = (settings: Partial<ContentProcessorTranscodeTemplateUpdateDto['TranscodeSettings']>) => {
        setTemplateForm((current) => current ? {
            ...current,
            TranscodeSettings: {
                ...current.TranscodeSettings,
                ...settings
            }
        } : current);
    };

    const updateTemplateProgressSettings = (settings: Partial<ContentProcessorTranscodeTemplateUpdateDto['ProgressSettings']>) => {
        setTemplateForm((current) => current ? {
            ...current,
            ProgressSettings: {
                ...current.ProgressSettings,
                ...settings
            }
        } : current);
    };

    const renderTemplateEditor = () => templateForm ? (
        <form onSubmit={saveTemplate} className={styles.templateEditor}>
            <div className={styles.templateHelp}>
                <strong>Known-good presets</strong>
                <span>Standard MP4: 1080p, 5500k target, 7500k max, 15000k buffer, H.264 high 4.1, AAC 128k.</span>
                <span>Mobile MP4: 540p, 1400k target, 1800k max, 3600k buffer, H.264 main 3.1, AAC 96k.</span>
                <a href="https://trac.ffmpeg.org/wiki/Encode/H.264" target="_blank" rel="noreferrer">FFmpeg H.264 encoding guide</a>
            </div>

            <div className={styles.templateGrid}>
                <div className={styles.field}>
                    <label htmlFor="template-name">Template name</label>
                    <input
                        id="template-name"
                        value={templateForm.Name}
                        onChange={(event) => setTemplateForm({ ...templateForm, Name: event.target.value })}
                        maxLength={200}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-worker-mode">Template worker mode</label>
                    <select
                        id="template-worker-mode"
                        value={templateForm.WorkerMode ?? standardRole}
                        onChange={(event) => updateTemplateWorkerMode(Number(event.target.value))}
                    >
                        {workerRoles.map((role) => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                    </select>
                    <span className={styles.muted}>Mode controls which queue the worker polls; output is derived from this selection.</span>
                </div>
                <div className={styles.field}>
                    <label>Template output</label>
                    <div>{formatOutputFormat(templateForm.OutputFormat)}</div>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-container">Container</label>
                    <select
                        id="template-container"
                        value={templateForm.TranscodeSettings.ContainerFormat}
                        onChange={(event) => updateTemplateTranscodeSettings({ ContainerFormat: Number(event.target.value) })}
                    >
                        {containerFormatOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-video-codec">Video codec</label>
                    <select
                        id="template-video-codec"
                        value={templateForm.TranscodeSettings.VideoCodec}
                        onChange={(event) => updateTemplateTranscodeSettings({ VideoCodec: Number(event.target.value) })}
                    >
                        {videoCodecOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-pixel-format">Pixel format</label>
                    <select
                        id="template-pixel-format"
                        value={templateForm.TranscodeSettings.PixelFormat}
                        onChange={(event) => updateTemplateTranscodeSettings({ PixelFormat: Number(event.target.value) })}
                    >
                        {pixelFormatOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-profile">H.264 profile</label>
                    <select
                        id="template-profile"
                        value={templateForm.TranscodeSettings.VideoProfile}
                        onChange={(event) => updateTemplateTranscodeSettings({ VideoProfile: Number(event.target.value) })}
                    >
                        {videoProfileOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <span className={styles.muted}>Standard uses high; mobile uses main.</span>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-level">H.264 level</label>
                    <select
                        id="template-level"
                        value={templateForm.TranscodeSettings.VideoLevel}
                        onChange={(event) => updateTemplateTranscodeSettings({ VideoLevel: Number(event.target.value) })}
                    >
                        {videoLevelOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <span className={styles.muted}>Standard uses 4.1; mobile uses 3.1.</span>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-audio-codec">Audio codec</label>
                    <select
                        id="template-audio-codec"
                        value={templateForm.TranscodeSettings.AudioCodec}
                        onChange={(event) => updateTemplateTranscodeSettings({ AudioCodec: Number(event.target.value) })}
                    >
                        {audioCodecOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-height">Output height</label>
                    <select
                        id="template-height"
                        value={templateForm.TranscodeSettings.OutputHeight}
                        onChange={(event) => updateTemplateTranscodeSettings({ OutputHeight: Number(event.target.value) })}
                    >
                        {outputHeightOptions.map((height) => (
                            <option key={height} value={height}>{height}p</option>
                        ))}
                    </select>
                    <span className={styles.muted}>Recommended: 1080 standard, 540 mobile.</span>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-video-bitrate">Video bitrate kbps</label>
                    <input
                        id="template-video-bitrate"
                        type="number"
                        min={templateLimits.videoBitrate.min}
                        max={templateLimits.videoBitrate.max}
                        value={templateForm.TranscodeSettings.VideoBitrateKilobits}
                        onChange={(event) => updateTemplateTranscodeSettings({ VideoBitrateKilobits: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-video-maxrate">Video max rate kbps</label>
                    <input
                        id="template-video-maxrate"
                        type="number"
                        min={templateLimits.videoBitrate.min}
                        max={templateLimits.videoBitrate.max}
                        value={templateForm.TranscodeSettings.VideoMaxRateKilobits}
                        onChange={(event) => updateTemplateTranscodeSettings({ VideoMaxRateKilobits: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-video-buffer">Video buffer kb</label>
                    <input
                        id="template-video-buffer"
                        type="number"
                        min={templateLimits.videoBitrate.min}
                        max={templateLimits.videoBitrate.max}
                        value={templateForm.TranscodeSettings.VideoBufferSizeKilobits}
                        onChange={(event) => updateTemplateTranscodeSettings({ VideoBufferSizeKilobits: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-frame-rate">Frame rate</label>
                    <input
                        id="template-frame-rate"
                        type="number"
                        min={templateLimits.frameRate.min}
                        max={templateLimits.frameRate.max}
                        value={templateForm.TranscodeSettings.FrameRate}
                        onChange={(event) => updateTemplateTranscodeSettings({ FrameRate: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-keyframe">Keyframe interval frames</label>
                    <input
                        id="template-keyframe"
                        type="number"
                        min={templateLimits.keyFrameInterval.min}
                        max={templateLimits.keyFrameInterval.max}
                        value={templateForm.TranscodeSettings.KeyFrameIntervalFrames}
                        onChange={(event) => updateTemplateTranscodeSettings({ KeyFrameIntervalFrames: Number(event.target.value) })}
                    />
                    <span className={styles.muted}>Recommended: 60 for 30fps outputs.</span>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-scene-threshold">Scene-change threshold</label>
                    <input
                        id="template-scene-threshold"
                        type="number"
                        min={templateLimits.sceneChangeThreshold.min}
                        max={templateLimits.sceneChangeThreshold.max}
                        value={templateForm.TranscodeSettings.SceneChangeThreshold}
                        onChange={(event) => updateTemplateTranscodeSettings({ SceneChangeThreshold: Number(event.target.value) })}
                    />
                    <span className={styles.muted}>Recommended: 0 to preserve fixed GOP cadence.</span>
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-audio-bitrate">Audio bitrate kbps</label>
                    <input
                        id="template-audio-bitrate"
                        type="number"
                        min={templateLimits.audioBitrate.min}
                        max={templateLimits.audioBitrate.max}
                        value={templateForm.TranscodeSettings.AudioBitrateKilobits}
                        onChange={(event) => updateTemplateTranscodeSettings({ AudioBitrateKilobits: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-audio-channels">Audio channels</label>
                    <input
                        id="template-audio-channels"
                        type="number"
                        min={templateLimits.audioChannels.min}
                        max={templateLimits.audioChannels.max}
                        value={templateForm.TranscodeSettings.AudioChannels}
                        onChange={(event) => updateTemplateTranscodeSettings({ AudioChannels: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-audio-sample-rate">Audio sample rate Hz</label>
                    <input
                        id="template-audio-sample-rate"
                        type="number"
                        min={templateLimits.audioSampleRate.min}
                        max={templateLimits.audioSampleRate.max}
                        value={templateForm.TranscodeSettings.AudioSampleRate}
                        onChange={(event) => updateTemplateTranscodeSettings({ AudioSampleRate: Number(event.target.value) })}
                    />
                    <span className={styles.muted}>Recommended: 48000Hz.</span>
                </div>
                <label className={styles.inlineCheckbox} htmlFor="template-fast-start">
                    <input
                        id="template-fast-start"
                        type="checkbox"
                        checked={templateForm.TranscodeSettings.EnableFastStart}
                        onChange={(event) => updateTemplateTranscodeSettings({ EnableFastStart: event.target.checked })}
                    />
                    Enable MP4 fast start
                </label>
                <label className={styles.inlineCheckbox} htmlFor="template-active">
                    <input
                        id="template-active"
                        type="checkbox"
                        checked={templateForm.IsActive}
                        onChange={(event) => setTemplateForm({ ...templateForm, IsActive: event.target.checked })}
                    />
                    Active for worker assignment
                </label>
                <label className={styles.inlineCheckbox} htmlFor="template-default">
                    <input
                        id="template-default"
                        type="checkbox"
                        checked={templateForm.IsDefault}
                        onChange={(event) => setTemplateForm({ ...templateForm, IsDefault: event.target.checked })}
                    />
                    Default for mode bootstrap
                </label>
                <label className={styles.inlineCheckbox} htmlFor="template-progress-enabled">
                    <input
                        id="template-progress-enabled"
                        type="checkbox"
                        checked={templateForm.ProgressSettings.EventsEnabled}
                        onChange={(event) => updateTemplateProgressSettings({ EventsEnabled: event.target.checked })}
                    />
                    Emit progress events
                </label>
                <label className={styles.inlineCheckbox} htmlFor="template-progress-first">
                    <input
                        id="template-progress-first"
                        type="checkbox"
                        checked={templateForm.ProgressSettings.EmitFirstEvent}
                        onChange={(event) => updateTemplateProgressSettings({ EmitFirstEvent: event.target.checked })}
                    />
                    Emit first progress event
                </label>
                <div className={styles.field}>
                    <label htmlFor="template-progress-interval">Progress interval seconds</label>
                    <input
                        id="template-progress-interval"
                        type="number"
                        min={templateLimits.progressInterval.min}
                        max={templateLimits.progressInterval.max}
                        value={templateForm.ProgressSettings.IntervalSeconds}
                        onChange={(event) => updateTemplateProgressSettings({ IntervalSeconds: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-progress-delta">Progress minimum percent delta</label>
                    <input
                        id="template-progress-delta"
                        type="number"
                        min={templateLimits.percentDelta.min}
                        max={templateLimits.percentDelta.max}
                        step="0.1"
                        value={templateForm.ProgressSettings.MinPercentDelta}
                        onChange={(event) => updateTemplateProgressSettings({ MinPercentDelta: Number(event.target.value) })}
                    />
                </div>
                <div className={styles.field}>
                    <label htmlFor="template-notes">Template notes</label>
                    <textarea
                        id="template-notes"
                        value={templateForm.Notes ?? ''}
                        onChange={(event) => setTemplateForm({ ...templateForm, Notes: event.target.value })}
                        maxLength={2000}
                    />
                </div>
            </div>

            <div className={styles.actions}>
                <button type="submit" className={styles.primary} disabled={isSaving}>
                    Save Template
                </button>
                <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => {
                        setSelectedTemplateId(null);
                        setTemplateForm(null);
                    }}
                    disabled={isSaving}
                >
                    Cancel
                </button>
            </div>
        </form>
    ) : null;

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Content Processing Config</h1>
                    <p>Manage remote transcoder defaults and worker-specific overrides.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}
            {successMessage ? <div className={styles.muted}>{successMessage}</div> : null}

            {isLoading || !form ? (
                <section className={styles.panel}>
                    <div className={styles.muted}>Loading content processor configuration...</div>
                </section>
            ) : (
                <div className={styles.content}>
                    <section className={styles.panel}>
                        <h2>Target</h2>
                        <div className={styles.field}>
                            <label htmlFor="processor-target">Configuration target</label>
                            <select
                                id="processor-target"
                                value={selectedTargetId}
                                onChange={(event) => setSelectedTargetId(event.target.value)}
                            >
                                <option value={globalTargetId}>Global defaults</option>
                                {workers.map((worker) => (
                                    <option key={worker.WorkerId} value={worker.WorkerId}>
                                        {worker.DisplayName || worker.WorkerId}{worker.IsConfigured ? '' : ' (not configured)'}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label>Registered workers</label>
                            <div>{workers.length}</div>
                        </div>
                    </section>

                    <section className={styles.panel}>
                        <h2>{isGlobalSelected ? 'Global Processing Settings' : 'Worker Settings'}</h2>
                        <form onSubmit={saveConfiguration}>
                            {isGlobalSelected ? renderGlobalFields() : renderWorkerFields()}
                            <div className={styles.actions}>
                                <button type="submit" className={styles.primary} disabled={isSaving || (!isGlobalSelected && !selectedWorker)}>
                                    Save
                                </button>
                                <button type="button" className={styles.secondary} onClick={loadConfiguration} disabled={isSaving}>
                                    Reload
                                </button>
                                {!isGlobalSelected && selectedWorker ? (
                                    <button type="button" className={styles.secondary} onClick={hideSelectedWorker} disabled={isSaving}>
                                        Hide Worker
                                    </button>
                                ) : null}
                            </div>
                        </form>
                    </section>

                    <section className={styles.panel}>
                        <h2>Quick Controls</h2>
                        <div className={styles.actions}>
                            <button type="button" className={styles.secondary} onClick={() => pauseForMinutes(5)} disabled={isSaving || (!isGlobalSelected && !selectedWorker)}>
                                Pause 5 min
                            </button>
                            <button type="button" className={styles.secondary} onClick={() => pauseForMinutes(15)} disabled={isSaving || (!isGlobalSelected && !selectedWorker)}>
                                Pause 15 min
                            </button>
                            <button type="button" className={styles.secondary} onClick={() => pauseForMinutes(60)} disabled={isSaving || (!isGlobalSelected && !selectedWorker)}>
                                Pause 1 hour
                            </button>
                            <button type="button" className={styles.primary} onClick={resumeNow} disabled={isSaving || (!isGlobalSelected && !selectedWorker)}>
                                Resume
                            </button>
                        </div>
                        {isGlobalSelected ? (
                            <>
                                <div className={styles.field}>
                                    <label>Status</label>
                                    <div>{configuration?.IsPollingEnabled ? 'Processing enabled globally' : 'Processing disabled globally'}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Pause Until</label>
                                    <div>{formatDateTime(configuration?.PauseUntilUtc)}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Last Worker Heartbeat</label>
                                    <div>{formatDateTime(configuration?.LastWorkerHeartbeatUtc)}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Last Worker ID</label>
                                    <div>{configuration?.LastWorkerId ?? '-'}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Updated</label>
                                    <div>{formatDateTime(configuration?.UpdatedDate)} by {configuration?.UpdatedByUserName ?? '-'}</div>
                                </div>
                            </>
                        ) : selectedWorker ? (
                            <>
                                <div className={styles.field}>
                                    <label>Worker ID</label>
                                    <div>{selectedWorker.WorkerId}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Configuration status</label>
                                    <div>{selectedWorker.ConfigurationStatusMessage ?? selectedWorker.ConfigurationStatus}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Worker mode</label>
                                    <div>{formatWorkerRole(selectedWorker.WorkerMode)}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Template</label>
                                    <div>{selectedWorker.AssignedTranscodeTemplateName ?? 'Not configured'}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Effective processing</label>
                                    <div>{selectedWorker.CanProcessWork ? 'Can process work' : 'Not processing'}; idle check every {selectedWorker.EffectivePollingIntervalSeconds}s</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Effective pause until</label>
                                    <div>{formatDateTime(selectedWorker.EffectivePauseUntilUtc)}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Last seen</label>
                                    <div>{formatDateTime(selectedWorker.LastSeenUtc)}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Last error</label>
                                    <div>{formatDateTime(selectedWorker.LastErrorUtc)}</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Last 7 days</label>
                                    <div>{selectedWorker.TotalEventsLast7Days} events, {selectedWorker.TotalLogsLast7Days} logs, {selectedWorker.ErrorsLast7Days} errors</div>
                                </div>
                                <div className={styles.field}>
                                    <label>Updated</label>
                                    <div>{formatDateTime(selectedWorker.UpdatedDate)} by {selectedWorker.UpdatedByUserName ?? '-'}</div>
                                </div>
                            </>
                        ) : null}
                    </section>

                    <section className={`${styles.panel} ${styles.fullWidthPanel}`}>
                        <h2>Transcode Templates</h2>
                        <div className={styles.actions}>
                            <button type="button" className={styles.secondary} onClick={() => startTemplateFromPreset(standardRole)} disabled={isSaving}>
                                New Standard Preset
                            </button>
                            <button type="button" className={styles.secondary} onClick={() => startTemplateFromPreset(mobileBackfillRole)} disabled={isSaving}>
                                New Mobile Preset
                            </button>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="template-to-edit">Template to edit</label>
                            <select
                                id="template-to-edit"
                                value={selectedTemplateId ?? ''}
                                onChange={(event) => selectTemplateForEdit(event.target.value)}
                            >
                                <option value="">Choose a template...</option>
                                {templates.map((template) => (
                                    <option key={template.Id} value={template.Id}>
                                        {template.Name}{template.IsActive ? '' : ' (inactive)'}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {renderTemplateEditor()}
                        {templates.length === 0 ? (
                            <div className={styles.muted}>No templates configured.</div>
                        ) : (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Mode</th>
                                        <th>Output</th>
                                        <th>Video</th>
                                        <th>Progress</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {templates.map((template) => (
                                        <tr key={template.Id}>
                                            <td>{template.Name}{template.IsDefault ? ' (default)' : ''}{template.IsActive ? '' : ' (inactive)'}</td>
                                            <td>{formatWorkerRole(template.WorkerMode)}</td>
                                            <td>{formatOutputFormat(template.OutputFormat)} / {formatContainerFormat(template.TranscodeSettings.ContainerFormat)}</td>
                                            <td>
                                                {template.TranscodeSettings.OutputHeight}p, {template.TranscodeSettings.VideoBitrateKilobits}k, {template.TranscodeSettings.FrameRate}fps,
                                                {' '}{formatVideoProfile(template.TranscodeSettings.VideoProfile)} {formatVideoLevel(template.TranscodeSettings.VideoLevel)}
                                            </td>
                                            <td>{template.ProgressSettings.EventsEnabled ? `${template.ProgressSettings.IntervalSeconds}s / ${template.ProgressSettings.MinPercentDelta}%` : 'Disabled'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default AdminContentProcessingConfig;
